import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  Building2, Users, Wallet, AlertCircle, TrendingUp, MessageSquare,
  Bed, IndianRupee, Activity, Mail, Phone, ArrowUpRight, Eye, RefreshCw, Plus
} from "lucide-react";
import { Link } from "react-router-dom";

export default function OperatorDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [tab, setTab] = useState("overview"); // overview | properties | tenants | commission

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") { nav("/login", { state: { from: "/operator" }}); return; }
    if (!["owner", "admin", "super_admin"].includes(user.role)) { nav("/dashboard"); return; }
    refresh();
    // eslint-disable-next-line
  }, [user, loading]);

  const refresh = async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/operator/overview"),
        api.get("/operator/tenants"),
        api.get("/operator/commission-ledger"),
      ]);
      setOverview(a.data);
      setTenants(b.data || []);
      setLedger(c.data || null);
    } catch (e) { /* */ }
  };

  if (loading || !overview) {
    return (
      <div className="min-h-screen grid place-items-center" style={{background:"#0D1B2A"}}>
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto text-[#34C97B]" size={28}/>
          <div className="mt-3 font-semibold text-white/70">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  const k = overview.kpis;
  const maxChart = Math.max(1, ...overview.collection_chart.map(c => c.amount));

  const Kpi = ({ icon: Icon, label, value, prefix = "", suffix = "", color = "emerald", trend, testid }) => (
    <div
      className="rounded-2xl p-5 hover:scale-[1.01] transition"
      style={{
        background: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(14px)",
        border: "1px solid rgba(52,201,123,0.25)",
      }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl grid place-items-center ${
          color === "emerald" ? "bg-[#34C97B]/15 text-[#34C97B]" :
          color === "amber" ? "bg-amber-500/15 text-amber-300" :
          color === "rose" ? "bg-rose-500/15 text-rose-300" :
          "bg-blue-500/15 text-blue-300"
        }`}>
          <Icon size={20}/>
        </div>
        {trend && (
          <span className="text-xs font-semibold text-[#34C97B] inline-flex items-center gap-0.5"><ArrowUpRight size={12}/>{trend}</span>
        )}
      </div>
      <div className="font-display font-black text-3xl text-white mt-3" style={{letterSpacing:"-0.02em"}}>
        {prefix}{value.toLocaleString()}<span className="text-base font-bold text-white/40">{suffix}</span>
      </div>
      <div className="text-xs font-semibold uppercase tracking-wider text-white/55 mt-1">{label}</div>
    </div>
  );

  const payBadge = (s) => ({
    paid: "bg-[#34C97B]/15 text-[#34C97B] border-[#34C97B]/30",
    pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    overdue: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  })[s] || "bg-white/5 text-white/60 border-white/10";

  return (
    <div className="panel-shell min-h-screen" data-testid="operator-dashboard">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#34C97B]">Operator Dashboard</div>
            <h1 className="font-display font-black text-3xl md:text-4xl mt-1 tracking-tight text-white">
              Welcome back, {(user.name || "").split(" ")[0]} 👋
            </h1>
            <p className="text-white/55 text-sm mt-1">Your portfolio at a glance — properties, tenants, collections, dues.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={refresh} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/15 hover:border-[#34C97B]/50 text-white px-3 py-2 rounded-xl text-sm font-semibold transition" data-testid="refresh-btn">
              <RefreshCw size={14}/> Refresh
            </button>
            <Link to="/maintenance" className="inline-flex items-center gap-1.5 bg-white/5 border border-white/15 hover:border-[#34C97B]/50 text-white px-3 py-2 rounded-xl text-sm font-semibold transition" data-testid="maintenance-btn">
              <AlertCircle size={14}/> Tickets
            </Link>
            <Link to="/list-property" className="inline-flex items-center gap-1.5 bg-[#34C97B] hover:bg-[#22B36A] text-white px-4 py-2 rounded-xl text-sm font-semibold transition">
              <Plus size={14}/> Add property
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-white/5 border border-white/10 rounded-xl mb-6 max-w-md">
          {[
            { v: "overview", l: "Overview" },
            { v: "properties", l: `Properties (${overview.properties.length})` },
            { v: "tenants", l: `Tenants (${tenants.length})` },
            { v: "commission", l: "Commission" },
          ].map(t => (
            <button key={t.v} onClick={()=>setTab(t.v)} className={`flex-1 text-sm font-semibold py-2 rounded-lg transition ${tab===t.v ? "bg-[#34C97B] text-white shadow" : "text-white/60 hover:text-white"}`} data-testid={`tab-${t.v}`}>{t.l}</button>
          ))}
        </div>

        {tab === "overview" && (
          <>
            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Kpi icon={Wallet} label="Collected (30d)" value={k.monthly_collected} prefix="₹" color="emerald" testid="kpi-collected"/>
              <Kpi icon={Bed} label="Occupancy" value={k.occupancy_pct} suffix="%" color="amber" trend={`${k.occupied_beds}/${k.total_beds} beds`} testid="kpi-occupancy"/>
              <Kpi icon={AlertCircle} label="Open Dues" value={k.open_dues_total} prefix="₹" color="rose" testid="kpi-dues"/>
              <Kpi icon={Building2} label="Properties" value={k.properties} color="blue" testid="kpi-properties"/>
            </div>

            {/* Secondary row */}
            <div className="grid sm:grid-cols-3 gap-4 mt-4">
              <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center justify-between" data-testid="kpi-paid-invoices">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Paid invoices (30d)</div>
                  <div className="font-display font-black text-2xl text-[#111111] mt-1">{k.paid_invoices_30d}</div>
                </div>
                <Activity size={28} className="text-[#34C97B] opacity-30"/>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center justify-between" data-testid="kpi-enquiries">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Open enquiries</div>
                  <div className="font-display font-black text-2xl text-[#111111] mt-1">{k.open_enquiries}</div>
                </div>
                <MessageSquare size={28} className="text-blue-500 opacity-30"/>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center justify-between" data-testid="kpi-tickets">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Open tickets</div>
                  <div className="font-display font-black text-2xl text-[#111111] mt-1">{k.open_tickets}</div>
                </div>
                <AlertCircle size={28} className="text-amber-500 opacity-30"/>
              </div>
            </div>

            {/* Collections chart */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 mt-6" data-testid="collections-chart">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="font-display font-black text-xl text-[#111111]">Collections — last 7 days</h3>
                  <p className="text-sm text-gray-500">Daily rent collected from your properties</p>
                </div>
                <TrendingUp size={22} className="text-[#34C97B]"/>
              </div>
              <div className="flex items-end gap-3 h-40">
                {overview.collection_chart.map((c, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2" title={`${c.date}: ₹${c.amount}`}>
                    <div className="text-xs font-bold text-gray-500">₹{c.amount}</div>
                    <div className="w-full bg-gradient-to-t from-[#34C97B] to-[#7BE3A7] rounded-t-lg transition-all hover:opacity-80"
                         style={{ height: `${Math.max(8, (c.amount / maxChart) * 100)}%`, minHeight: 8 }}/>
                    <div className="text-xs text-gray-400">{c.date.slice(5)}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {tab === "properties" && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="properties-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Property</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">City</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Beds</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Occupancy</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Rent potential</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {overview.properties.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50 transition" data-testid={`property-row-${p.id}`}>
                    <td className="px-5 py-3 font-semibold text-[#111111]">{p.title}</td>
                    <td className="px-5 py-3 text-gray-600">{p.city || "—"}</td>
                    <td className="px-5 py-3"><span className="text-[#22B36A] font-bold">{p.occupied_beds}</span><span className="text-gray-400"> / {p.total_beds}</span></td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#34C97B]" style={{width:`${p.occupancy_pct}%`}}/>
                        </div>
                        <span className="text-xs font-bold text-gray-600">{p.occupancy_pct}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 font-bold text-[#111111]">₹{p.monthly_rent_potential.toLocaleString()}</td>
                    <td className="px-5 py-3">
                      <Link to={`/properties/${p.id}`} className="inline-flex items-center gap-1 text-[#22B36A] font-semibold hover:underline text-xs"><Eye size={12}/> View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "tenants" && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="tenants-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Tenant</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Property</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Rent</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Payment</th>
                  <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Agreement</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tenants.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-12 text-center text-gray-500">No tenants yet</td></tr>
                ) : tenants.map(t => (
                  <tr key={t.booking_id} className="hover:bg-gray-50 transition" data-testid={`tenant-row-${t.booking_id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#34C97B] to-[#22B36A] grid place-items-center text-white font-bold">
                          {(t.student_name || "?")[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-[#111111]">{t.student_name}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            {t.student_email && <span className="flex items-center gap-0.5"><Mail size={10}/>{t.student_email}</span>}
                            {t.student_phone && <span className="flex items-center gap-0.5"><Phone size={10}/>{t.student_phone}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="text-[#111111] font-medium">{t.property_title}</div>
                      <div className="text-xs text-gray-500">{t.city}</div>
                    </td>
                    <td className="px-5 py-3 font-bold text-[#111111]">₹{(t.rent || 0).toLocaleString()}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${payBadge(t.payment_status)}`}>{t.payment_status}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${t.agreement_status === "signed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-700 border-gray-200"}`}>{t.agreement_status}</span>
                    </td>
                    <td className="px-5 py-3">
                      <Link to="/messages" className="text-[#22B36A] font-semibold hover:underline text-xs">Message</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "commission" && ledger && (
          <div className="space-y-4" data-testid="commission-tab">
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Gross sales</div>
                <div className="font-display font-black text-2xl text-[#111111] mt-1">₹{ledger.summary.total_gross.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Hostro commission</div>
                <div className="font-display font-black text-2xl text-[#22B36A] mt-1">₹{ledger.summary.total_commission.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Vendor payout</div>
                <div className="font-display font-black text-2xl text-[#111111] mt-1">₹{ledger.summary.total_payout.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Transactions</div>
                <div className="font-display font-black text-2xl text-[#111111] mt-1">{ledger.summary.transactions}</div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Type</th>
                    <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Vendor</th>
                    <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Date</th>
                    <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Gross</th>
                    <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Rate</th>
                    <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Commission</th>
                    <th className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Payout</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {ledger.rows.length === 0 ? (
                    <tr><td colSpan={7} className="px-5 py-12 text-center text-gray-500">No commission transactions yet</td></tr>
                  ) : ledger.rows.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 capitalize"><span className={`text-xs font-bold px-2 py-1 rounded-full border ${r.type === "food" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{r.type}</span></td>
                      <td className="px-5 py-3 font-semibold text-[#111111]">{r.vendor_name}</td>
                      <td className="px-5 py-3 text-gray-500">{(r.date || "").slice(0,10)}</td>
                      <td className="px-5 py-3 font-bold">₹{r.order_total.toLocaleString()}</td>
                      <td className="px-5 py-3 text-gray-500">{r.commission_rate}</td>
                      <td className="px-5 py-3 font-bold text-[#22B36A]">₹{r.commission_amount.toLocaleString()}</td>
                      <td className="px-5 py-3 text-gray-700">₹{r.vendor_payout.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
